/** Bindable field types for personal_diagram (simple displayable values). */
export const PERSONAL_DIAGRAM_BINDABLE_TYPES = [
  "textbox",
  "textarea",
  "richtext_dynamic",
  "checkbox",
  "slider",
  "timer",
  "timestamp",
  "elapsed_time",
] as const;

/** True if `type` is allowed for personal_diagram field binding. */
export function isPersonalDiagramBindableType(type: unknown): boolean {
  return (
    typeof type === "string" &&
    (PERSONAL_DIAGRAM_BINDABLE_TYPES as readonly string[]).includes(type)
  );
}

export type PersonalDiagramBindableType =
  (typeof PERSONAL_DIAGRAM_BINDABLE_TYPES)[number];

export type PersonalDiagramStoredNode = {
  id: string;
  position: { x: number; y: number };
  authorText?: string;
  authorText__tid?: string;
  fieldKeys: string[];
};

export type PersonalDiagramStoredEdge = {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  /** Optional static author label (translatable). */
  label?: string;
  label__tid?: string;
  /** Bound activity field keys — resolved into the arrow label at runtime. */
  fieldKeys?: string[];
};

/**
 * Build the visible arrow-label string from optional static text + bound field keys.
 * Editor passes placeholders like `{key}`; runtime passes already-formatted values.
 */
export function composeEdgeLabelDisplay(
  staticLabel: string | undefined,
  boundParts: string[],
): string {
  const parts: string[] = [];
  const trimmed = typeof staticLabel === "string" ? staticLabel.trim() : "";
  if (trimmed) parts.push(trimmed);
  for (const part of boundParts) {
    const p = typeof part === "string" ? part.trim() : "";
    if (p) parts.push(p);
  }
  return parts.join(" · ");
}

export type PersonalDiagramContent = {
  nodes?: PersonalDiagramStoredNode[];
  edges?: PersonalDiagramStoredEdge[];
};

export function normalizePersonalDiagramContent(
  content: unknown,
): { nodes: PersonalDiagramStoredNode[]; edges: PersonalDiagramStoredEdge[] } {
  const c = (content && typeof content === "object" ? content : {}) as PersonalDiagramContent;
  const nodes = Array.isArray(c.nodes)
    ? c.nodes.map((n) => ({
        id: String(n.id),
        position: {
          x: Number(n.position?.x) || 0,
          y: Number(n.position?.y) || 0,
        },
        authorText: typeof n.authorText === "string" ? n.authorText : "",
        authorText__tid:
          typeof n.authorText__tid === "string" ? n.authorText__tid : undefined,
        fieldKeys: Array.isArray(n.fieldKeys)
          ? n.fieldKeys.map(String).filter(Boolean)
          : [],
      }))
    : [];
  const edges = Array.isArray(c.edges)
    ? c.edges.map((e) => ({
        id: String(e.id),
        source: String(e.source),
        target: String(e.target),
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        label: typeof e.label === "string" ? e.label : "",
        label__tid:
          typeof e.label__tid === "string" ? e.label__tid : undefined,
        fieldKeys: Array.isArray(e.fieldKeys)
          ? e.fieldKeys.map(String).filter(Boolean)
          : [],
      }))
    : [];
  return { nodes, edges };
}
